import {
  lazy, Suspense, useContext, useEffect, useLayoutEffect, useState,
} from "react";
import { Switch as ReactRouterDomSwitch, useLocation, useHistory } from "react-router-dom";
import { configure } from "mobx";

import { parse } from "query-string";
import { Input, Modal, Form } from "antd";
import { observer } from "mobx-react";
import { SuspenceFallbackComponent } from "./components/SuspenceFallback";
import { NavigationComponent } from "./components/Navigation";
import { CustomRoute } from "./components/CustomRoute";
import { PARAMS, Routes } from "./utils/routes";
import { INDEXING_OPERATOR_ROUTES, TAGBOARD_OPERATOR_ROUTES } from "./utils/operatorRoles";
import { isPublicOperatorPath, isPublicSquadRecapPath } from "./utils/publicOperatorPaths";

import { BasicButton, BasicContentWrapper, COLORS } from "./styled/basic";
import { FormToolbox } from "./styled/form";

import "react-calendar/dist/Calendar.css";
import "./styles/main.scss";

import { AppServiceContext } from "./services/app";
import { NavigationServiceInstance } from "./services/navigation";
import { SessionServiceInstance } from "./services/session";
import {
  primeOperatorAdminChatAudioOnUserGesture,
  resumeOperatorAdminChatAudioContextIfSuspended,
} from "./utils/operatorAdminChatSound";
import "./services/teams";
import "./services/stadiums";
import "./services/game";
import "./services/socket";
import "./services/notification";
import "./services/operatorNotificationFeed";

configure({
  enforceActions: "never",
});

const Switch = ReactRouterDomSwitch as any;

const LoginPage = lazy(() => import("./pages/public/login"));
const GamesPage = lazy(() => import("./pages/games"));
const GameHubPage = lazy(() => import("./pages/gameHub"));
const TeamsPage = lazy(() => import("./pages/teams"));
const TeamDetailPage = lazy(() => import("./pages/teams/TeamDetailPage"));
const StadiumPage = lazy(() => import("./pages/stadiums"));
const SeatsListPage = lazy(() => import("./pages/stadiums/SeatsList"));
const VisionPage = lazy(() => import("./pages/vision"));
const MappingPage = lazy(() => import("./pages/mapping"));
const MomentPage = lazy(() => import("./pages/moment"));
const UsersPage = lazy(() => import("./pages/users"));
const GameSeatsPage = lazy(() => import("./pages/gameSeats"));
const TemplatesPage = lazy(() => import("./pages/templates"));
const DownloadsPage = lazy(() => import("./pages/downloads"));
const PrintsPage = lazy(() => import("./pages/prints"));
const ProfilesPage = lazy(() => import("./pages/profiles"));
const InsightsPage = lazy(() => import("./pages/Insights"));
const EngagementPage = lazy(() => import("./pages/engagement"));
const EngagementFunnelPage = lazy(() => import("./pages/engagement/FunnelPage"));
const IndexingPage = lazy(() => import("./pages/indexing"));
const TriggeringPage = lazy(() => import("./pages/triggering"));
const CameraControlsPage = lazy(() => import("./pages/cameraControls"));
const ManageAccessPage = lazy(() => import("./pages/access"));
const ManageOperatorsPage = lazy(() => import("./pages/operators"));
const ContactOperatorsPage = lazy(() => import("./pages/operators/ContactOperatorsPage"));
const SchedulingPage = lazy(() => import("./pages/operators/SchedulingPage"));
const ManageTeamOperatorsPage = lazy(() => import("./pages/operators/ManageTeamOperatorsPage"));
const SchedulePage = lazy(() => import("./pages/operators/SchedulePage"));
const OperatorProfileViewPage = lazy(() => import("./pages/access/OperatorProfileView"));
const SupportPage = lazy(() => import("./pages/support"));
const AccountPage = lazy(() => import("./pages/account"));
const NotificationsPage = lazy(() => import("./pages/notifications"));
const TagboardAccountsPage = lazy(() => import("./pages/tagboardAccounts"));
const TagboardOAuthPage = lazy(() => import("./pages/tagboard/OAuth"));
const VideoboardPage = lazy(() => import("./pages/videoboard"));
const MonitoringPage = lazy(() => import("./pages/monitoring"));
const SnapsPage = lazy(() => import("./pages/snaps"));
const FilesCatalogPage = lazy(() => import("./pages/files"));
const FilesFoldersPage = lazy(() => import("./pages/files/FoldersPage"));
const FilesFolderContentsPage = lazy(() => import("./pages/files/FolderContentsPage"));
const SnapsGalleryPage = lazy(() => import("./pages/snapsGallery"));
const SnapsRecapPage = lazy(() => import("./pages/snapsRecap"));
const SnapsTrackerPage = lazy(() => import("./pages/snapsTracker"));
const SnapsTrackerStadiumDetailPage = lazy(() => import("./pages/snapsTracker/StadiumDetailPage"));
const SnapsTrackerOtherEventsDetailPage = lazy(
  () => import("./pages/snapsTracker/OtherEventsDetailPage"),
);
const SnapSquadPage = lazy(() => import("./pages/snapsSquad"));
const SnapsEventPage = lazy(() => import("./pages/snapsEvent"));
const SnapsInventoryPage = lazy(() => import("./pages/snapsInventory"));
const SnapsTeamsPage = lazy(() => import("./pages/snapsTeams"));
const SnapsTeamDetailPage = lazy(() => import("./pages/snapsTeamDetail"));
const CrowdAnalyticsPage = lazy(() => import("./pages/crowdAnalytics"));
const EmployeeManagementPage = lazy(() => import("./pages/employeeManagement"));
const AllEmployeesPage = lazy(() => import("./pages/employeeManagement/allEmployees"));
const SearchASeatPage = lazy(() => import("./pages/searchASeat"));
const LiveGameViewPage = lazy(() => import("./pages/liveGameView"));
const LoggingPage = lazy(() => import("./pages/logging"));
const LiveMomentsCleanupPage = lazy(() => import("./pages/liveMomentsCleanup"));
const GameRecapPage = lazy(() => import("./pages/gameRecap"));
const SeatViewPage = lazy(() => import("./pages/seatView"));
const PublicGameRecapPage = lazy(() => import("./pages/publicGameRecap"));
const PublicTeamEngagementPage = lazy(() => import("./pages/publicEngagement"));
const ParkingExpensePage = lazy(() => import("./pages/parkingExpense"));
const OperatorAvailabilityPage = lazy(() => import("./pages/operatorAvailability"));
const ClientTrackerPage = lazy(() => import("./pages/clientTracker"));
const ClientTrackerContactsPage = lazy(() => import("./pages/clientTracker/contacts"));
const ClientTrackerDetailPage = lazy(() => import("./pages/clientTracker/detail"));

export const App = observer((): JSX.Element => {
  const { search, pathname } = useLocation();
  const history = useHistory();
  useEffect(() => {
    NavigationServiceInstance.setHistory(history);
  }, []);

  /** Fallback when hosting collapses unknown paths to `/` — email can use `/?squadRecapToken=`. */
  useLayoutEffect(() => {
    const token = new URLSearchParams(search).get("squadRecapToken");
    if (token && pathname === Routes.LOGIN) {
      history.replace(`/squad-recap/${encodeURIComponent(token)}`);
    }
  }, [pathname, search, history]);

  /** Legacy operator recap links → dedicated SnapSquad portal. */
  useLayoutEffect(() => {
    if (!isPublicSquadRecapPath(pathname)) return;
    const isLocal = window.location.hostname === "localhost"
      || window.location.hostname === "127.0.0.1";
    if (isLocal) return;
    const segments = pathname.split("/").filter(Boolean);
    const token = segments[segments.length - 1];
    if (!token || token === "squad-recap" || token === "recap") return;
    const cfg = (window as { appConfig?: { snapsquadPortalUrl?: string } }).appConfig;
    const snapsquadBase = (cfg?.snapsquadPortalUrl || "https://snapsquad.mymomento.com").replace(/\/$/, "");
    window.location.replace(`${snapsquadBase}/squad-recap/${encodeURIComponent(token)}`);
  }, [pathname]);

  /**
   * Unlocks Web Audio for admin-chat chimes on first user interaction (autoplay policy).
   * Listeners attach immediately so a click before session email hydrates still primes audio.
   */
  useEffect(() => {
    const onGesture = (): void => {
      primeOperatorAdminChatAudioOnUserGesture();
    };
    window.addEventListener("pointerdown", onGesture, { capture: true, passive: true });
    window.addEventListener("touchstart", onGesture, { capture: true, passive: true });
    window.addEventListener("click", onGesture, { capture: true, passive: true });
    window.addEventListener("keydown", onGesture, { capture: true, passive: true });
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") {
        resumeOperatorAdminChatAudioContextIfSuspended();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pointerdown", onGesture, true);
      window.removeEventListener("touchstart", onGesture, true);
      window.removeEventListener("click", onGesture, true);
      window.removeEventListener("keydown", onGesture, true);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const { lastCameraId, lastCameraName } = useContext(AppServiceContext);
  const [mappingModal, setMappingModal] = useState(false);
  const [form] = Form.useForm<{id: string}>();

  const goToMapping = (id: string) => {
    setMappingModal(false);
    history.push(`${Routes.MAPPING}?${PARAMS.ID}=${id}`);
    form.resetFields();
  };

  const goToStadiums = () => {
    setMappingModal(false);
    history.push(Routes.STADIUM);
    form.resetFields();
  };

  const ADMIN_ONLY_ROUTES = [
    Routes.TEAMS,
    Routes.PROFILES,
    Routes.ENGAGEMENT,
    Routes.GAME_RECAP,
    Routes.SEAT_VIEW,
    Routes.INDEXING,
    Routes.SUPPORT,
    Routes.MANAGE_ACCESS,
    Routes.USERS,
    Routes.INSIGHTS,
    Routes.STADIUM,
    Routes.STADIUM_SEATS,
    Routes.MONITORING,
    Routes.VISION,
    Routes.TAGBOARD_ACCOUNTS,
    Routes.SNAPS,
    Routes.FILES,
    Routes.SNAPS_GALLERY,
    Routes.SNAPS_TRACKER,
    Routes.SNAPS_TRACKER_STADIUM,
    Routes.SNAPS_SQUAD,
    Routes.SNAPS_INVENTORY,
    Routes.SNAPS_TEAMS,
    Routes.SNAPS_TEAM_DETAIL,
    Routes.SNAPS_EVENT,
    Routes.CLIENT_TRACKER,
    Routes.CLIENT_TRACKER_CONTACTS,
    Routes.CLIENT_TRACKER_DETAIL,
    Routes.CROWD_ANALYTICS,
    Routes.TEMPLATES,
    Routes.DOWNLOADS,
    Routes.PRINTS,
    Routes.TRIGGERING,
    Routes.OPERATORS,
    Routes.CONTACT_OPERATORS,
    Routes.SCHEDULING,
    Routes.EMPLOYEE_MANAGEMENT,
    Routes.EMPLOYEE_MANAGEMENT_ALL,
    Routes.SEARCH_A_SEAT,
    Routes.LIVE_GAME_VIEW,
  ];

  useEffect(() => {
    const {
      isSuperAdmin,
      isOwnerAdmin,
      isIndexingOnlyOperator,
      isTagboardOnlyOperator,
      EmailAddress,
      canViewScheduleNav,
      defaultLandingRoute,
      profileComplete,
    } = SessionServiceInstance;
    if (isPublicOperatorPath(pathname)) return;
    if (!EmailAddress) return;

    if (profileComplete === false && pathname !== Routes.ACCOUNT) {
      history.replace(Routes.ACCOUNT);
      return;
    }

    if (isIndexingOnlyOperator) {
      const allowed = INDEXING_OPERATOR_ROUTES.some(
        (r) => pathname === r || pathname.startsWith(`${r}/`),
      );
      if (!allowed) {
        history.replace(defaultLandingRoute);
      }
      return;
    }

    if (isTagboardOnlyOperator) {
      const allowed = TAGBOARD_OPERATOR_ROUTES.some(
        (r) => pathname === r || pathname.startsWith(`${r}/`),
      );
      if (!allowed) {
        history.replace(defaultLandingRoute);
      }
      return;
    }

    if (EmailAddress && !isSuperAdmin) {
      const isOperatorProfile = pathname.startsWith(Routes.MANAGE_ACCESS_PROFILE);
      if (isOperatorProfile) {
        if (!canViewScheduleNav) {
          history.replace(Routes.GAMES);
        }
        return;
      }
      const isAdminRoute = ADMIN_ONLY_ROUTES.some(
        (r) => pathname === r || pathname.startsWith(`${r}/`),
      );
      if (isAdminRoute) {
        history.replace(Routes.GAMES);
      }
    }

    if (
      !isOwnerAdmin
      && (pathname === Routes.LOGGING || pathname === Routes.LIVE_MOMENTS_CLEANUP)
    ) {
      history.replace(isSuperAdmin ? Routes.GAMES : defaultLandingRoute);
    }
  }, [pathname, history, SessionServiceInstance.profileComplete]);

  useEffect(() => {
    if (pathname.includes(Routes.MAPPING)) {
      const parsed = parse(search);
      const id = parsed[PARAMS.ID];
      if (!id) {
        setMappingModal(true);
        history.goBack();
      }
    }
  }, [search, pathname]);

  return (
    <BasicContentWrapper padding="0">
      <Suspense fallback={<SuspenceFallbackComponent />}>
        {pathname !== Routes.VISION
          && pathname !== Routes.CAMERA_CONTROLS
          && pathname !== Routes.MAPPING
          && !pathname.startsWith("/game/")
          && pathname !== Routes.TAGBOARD_OAUTH
          && !isPublicOperatorPath(pathname)
          && pathname !== Routes.GAME_RECAP
          && <NavigationComponent />}

        <Modal
          visible={mappingModal}
          title="Enter Camera Id to proceed to the mapping or go to Stadium page"
          width="50%"
          style={{ minWidth: "600px", maxWidth: "1000px" }}
          okText="Submit"
          footer={null}
          onCancel={() => setMappingModal(false)}
        >
          <Form form={form} onFinish={(values) => goToMapping(values.id)}>
            <Form.Item
              label="Camera Id"
              name="id"
              rules={[{ required: true, message: "Please, enter the Camera Id!" }]}
            >
              <Input />
            </Form.Item>
            <FormToolbox>
              {lastCameraId && lastCameraName && (
                <BasicButton
                  type="button"
                  color={COLORS.WHITE}
                  backgroundColor={COLORS.BLUE_MAIN}
                  onClick={() => goToMapping(lastCameraId)}
                  style={{ marginRight: "auto", marginLeft: 0 }}
                >
                  {`Go to last camera (${lastCameraName})`}
                </BasicButton>
              )}

              <BasicButton type="button" onClick={goToStadiums}>Go to Stadiums page</BasicButton>
              <BasicButton type="submit" color={COLORS.WHITE} backgroundColor={COLORS.BLUE_MAIN}>Proceed to mapping</BasicButton>

            </FormToolbox>
          </Form>
        </Modal>

        <Switch>
          <CustomRoute exact path={Routes.LOGIN} Component={LoginPage} />
          <CustomRoute path={Routes.PUBLIC_ENGAGEMENT} Component={PublicTeamEngagementPage} />
          <CustomRoute path={Routes.PUBLIC_GAME_RECAP} Component={PublicGameRecapPage} />
          <CustomRoute exact path={Routes.TAGBOARD_OAUTH} Component={TagboardOAuthPage} />
          <CustomRoute path={Routes.VIDEOBOARD} Component={VideoboardPage} />
          <CustomRoute exact path={Routes.GAMES} Component={GamesPage} />
          <CustomRoute path={Routes.GAME_HUB} Component={GameHubPage} />
          <CustomRoute exact path={Routes.TEAM_DETAIL} Component={TeamDetailPage} />
          <CustomRoute exact path={Routes.TEAMS} Component={TeamsPage} />
          <CustomRoute exact path={Routes.STADIUM_SEATS} Component={SeatsListPage} />
          <CustomRoute exact path={Routes.STADIUM} Component={StadiumPage} />
          <CustomRoute exact path={Routes.MONITORING} Component={MonitoringPage} />
          <CustomRoute exact path={Routes.VISION} Component={VisionPage} />
          <CustomRoute exact path={Routes.MAPPING} Component={MappingPage} />
          <CustomRoute exact path={Routes.MOMENT} Component={MomentPage} />
          <CustomRoute exact path={Routes.CAMERA_CONTROLS} Component={CameraControlsPage} />
          <CustomRoute exact path={Routes.USERS} Component={UsersPage} />
          <CustomRoute exact path={Routes.GAMESEATS} Component={GameSeatsPage} />
          <CustomRoute exact path={Routes.TEMPLATES} Component={TemplatesPage} />
          <CustomRoute exact path={Routes.SNAPS} Component={SnapsPage} />
          <CustomRoute exact path={Routes.FILES} Component={FilesCatalogPage} />
          <CustomRoute exact path={Routes.FILES_MOMENTO} Component={FilesFoldersPage} />
          <CustomRoute path={Routes.FILES_MOMENTO_FOLDER} Component={FilesFolderContentsPage} />
          <CustomRoute exact path={Routes.FILES_TEAM} Component={FilesFoldersPage} />
          <CustomRoute path={Routes.FILES_TEAM_FOLDER} Component={FilesFolderContentsPage} />
          <CustomRoute exact path={Routes.SNAPS_GALLERY} Component={SnapsGalleryPage} />
          <CustomRoute path={Routes.SQUAD_RECAP} Component={SnapsRecapPage} />
          <CustomRoute path={Routes.SNAPS_RECAP} Component={SnapsRecapPage} />
          <CustomRoute path={Routes.PARKING_EXPENSE} Component={ParkingExpensePage} />
          <CustomRoute path={Routes.AVAILABILITY} Component={OperatorAvailabilityPage} />
          <CustomRoute exact path={Routes.SNAPS_TRACKER} Component={SnapsTrackerPage} />
          <CustomRoute
            exact
            path={Routes.SNAPS_TRACKER_OTHER}
            Component={SnapsTrackerOtherEventsDetailPage}
          />
          <CustomRoute
            path={Routes.SNAPS_TRACKER_STADIUM}
            Component={SnapsTrackerStadiumDetailPage}
          />
          <CustomRoute path={Routes.SNAPS_EVENT} Component={SnapsEventPage} />
          <CustomRoute exact path={Routes.SNAPS_TEAMS} Component={SnapsTeamsPage} />
          <CustomRoute path={Routes.SNAPS_TEAM_DETAIL} Component={SnapsTeamDetailPage} />
          <CustomRoute exact path={Routes.SNAPS_INVENTORY} Component={SnapsInventoryPage} />
          <CustomRoute exact path={Routes.SNAPS_SQUAD} Component={SnapSquadPage} />
          <CustomRoute path={Routes.SNAPS_SQUAD_MEMBER} Component={SnapSquadPage} />
          <CustomRoute exact path={Routes.CLIENT_TRACKER} Component={ClientTrackerPage} />
          <CustomRoute
            exact
            path={Routes.CLIENT_TRACKER_CONTACTS}
            Component={ClientTrackerContactsPage}
          />
          <CustomRoute path={Routes.CLIENT_TRACKER_DETAIL} Component={ClientTrackerDetailPage} />
          <CustomRoute path={Routes.CROWD_ANALYTICS} Component={CrowdAnalyticsPage} />
          <CustomRoute exact path={Routes.DOWNLOADS} Component={DownloadsPage} />
          <CustomRoute exact path={Routes.PRINTS} Component={PrintsPage} />
          <CustomRoute exact path={Routes.PROFILES} Component={ProfilesPage} />
          <CustomRoute exact path={Routes.INSIGHTS} Component={InsightsPage} />
          <CustomRoute exact path={Routes.ENGAGEMENT} Component={EngagementPage} />
          <CustomRoute exact path={Routes.ENGAGEMENT_FUNNEL} Component={EngagementFunnelPage} />
          <CustomRoute exact path={Routes.GAME_RECAP} Component={GameRecapPage} />
          <CustomRoute exact path={Routes.SEAT_VIEW} Component={SeatViewPage} />
          <CustomRoute exact path={Routes.INDEXING} Component={IndexingPage} />
          <CustomRoute exact path={Routes.LOGGING} Component={LoggingPage} />
          <CustomRoute
            exact
            path={Routes.LIVE_MOMENTS_CLEANUP}
            Component={LiveMomentsCleanupPage}
          />
          <CustomRoute exact path={Routes.TRIGGERING} Component={TriggeringPage} />
          <CustomRoute exact path={Routes.MANAGE_ACCESS} Component={ManageAccessPage} />
          <CustomRoute exact path={Routes.OPERATORS} Component={ManageOperatorsPage} />
          <CustomRoute exact path={Routes.CONTACT_OPERATORS} Component={ContactOperatorsPage} />
          <CustomRoute exact path={Routes.SCHEDULING} Component={SchedulingPage} />
          <CustomRoute
            exact
            path={Routes.MANAGE_TEAM_OPERATORS}
            Component={ManageTeamOperatorsPage}
          />
          <CustomRoute exact path={Routes.EMPLOYEE_MANAGEMENT} Component={EmployeeManagementPage} />
          <CustomRoute
            exact
            path={Routes.EMPLOYEE_MANAGEMENT_ALL}
            Component={AllEmployeesPage}
          />
          <CustomRoute exact path={Routes.SEARCH_A_SEAT} Component={SearchASeatPage} />
          <CustomRoute exact path={Routes.LIVE_GAME_VIEW} Component={LiveGameViewPage} />
          <CustomRoute exact path={Routes.SCHEDULE} Component={SchedulePage} />
          <CustomRoute
            exact
            path={`${Routes.MANAGE_ACCESS_PROFILE}/:email`}
            Component={OperatorProfileViewPage}
          />
          <CustomRoute exact path={Routes.SUPPORT} Component={SupportPage} />
          <CustomRoute exact path={Routes.ACCOUNT} Component={AccountPage} />
          <CustomRoute exact path={Routes.NOTIFICATIONS} Component={NotificationsPage} />
          <CustomRoute exact path={Routes.TAGBOARD_ACCOUNTS} Component={TagboardAccountsPage} />
        </Switch>
      </Suspense>
    </BasicContentWrapper>
  );
});
